"use client"

import React, { useState, useRef, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import {
  isAdminLeaderboardRole,
  isManagementRole,
  isEplPlayersAccessRole,
} from "../constants/roles"
import {
  DollarSign,
  Users,
  Briefcase,
  Receipt,
  Gift,
  CreditCard,
  Settings,
  User,
  ChevronDown,
  Coffee,
  BarChart2,
  Clock,
  Coins,
  BarChart,
  CoinsIcon,
  CreditCardIcon,
  Trophy,
  MessageSquare
} from "lucide-react"

const NavItem = ({ icon: Icon, label, href, isCollapsed, isActive, hasSubmenu, onMouseEnter, onMouseLeave, isOpen }) => {
  const handleClick = (e) => {
    if (hasSubmenu) {
      e.preventDefault()
    }
  }

  return (
    <div
      className="relative"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Link
        to={href}
        className={`flex items-center justify-between w-full pl-4 pr-8 py-2.5 rounded-lg transition-all duration-300 ${
          isActive 
            ? "bg-gradient-to-r from-gray-700 to-gray-600 text-white shadow-md" 
            : "text-gray-600 hover:bg-gradient-to-r hover:from-gray-600 hover:to-gray-500 hover:text-white hover:shadow-sm"
        }`}
        onClick={handleClick}
      >
        <div className="flex items-center space-x-3">
          <Icon size={20} strokeWidth={1.5} className={isActive ? "text-white" : "text-indigo-400"} />
          {!isCollapsed && <span className="font-medium text-sm">{label}</span>}
        </div>
        {hasSubmenu && !isCollapsed && (
          <ChevronDown
            size={16}
            className={`transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </Link>
    </div>
  )
}

export function AppSidebar() {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const [hoveredItem, setHoveredItem] = useState(null)
  const [userRole, setUserRole] = useState(null)
  const location = useLocation()
  
  // Refs for timeout management
  const hoverTimeoutRef = useRef(null)
  const leaveTimeoutRef = useRef(null)

  // Get user role from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserRole(parsedUser.role);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const handleItemHover = (itemTitle) => {
    if (!isCollapsed) {
      // Clear any existing leave timeout
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
        leaveTimeoutRef.current = null
      }
      
      // Clear any existing hover timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      
      // Set a delay before opening the dropdown
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredItem(itemTitle)
        hoverTimeoutRef.current = null
      }, 200) // 200ms delay before opening
    }
  }

  const handleItemLeave = () => {
    // Clear any existing hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    
    // Clear any existing leave timeout
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
    }
    
    // Set a delay before closing the dropdown
    leaveTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null)
      leaveTimeoutRef.current = null
    }, 300) // 300ms delay before closing
  }

  const handleDropdownEnter = (itemTitle) => {
    // Clear leave timeout when entering dropdown area
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current)
      leaveTimeoutRef.current = null
    }
    // Ensure dropdown stays open
    setHoveredItem(itemTitle)
  }

  const handleDropdownLeave = () => {
    // Start the leave timeout when leaving dropdown
    handleItemLeave()
  }

  // Cleanup timeouts on unmount
  React.useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current)
      }
    }
  }, [])

  const managementItems = [
    {
      title: "User Management",
      icon: Users,
      href: "/user-management",
    },
    {
      title: "Brownsville EPL Dashboard",
      icon: BarChart,
      href: "/",
    },
    {
      title: "Employee Management",
      icon: Briefcase,
      href: "/employee-management",
    },
    {
      title: "Token Reserves",
      icon: Coins,
      href: "/token-reserves",
    },
    {
      title: "Payroll Management",
      icon: Receipt,
      href: "/dashboard/payroll",
      hasSubmenu: true,
      subItems: [
        { title: "Payroll Settings", href: "/dashboard/payroll/salary", icon: Settings },
        { title: "Payroll Entry", href: "/dashboard/payroll/bonuses", icon: Gift },
      ],
    },
    {
      title: "Starting/Closing Tables Time",
      icon: Clock,
      href: "/starting-closing-tables-time",
    },
  ]

  const transactionItems = [
    {
      title: "Promos",
      icon: Gift,
      href: "/promos",
    },
    {
      title: "Petty Cash",
      icon: CreditCard,
      href: "/petty-cash",
    },
    {
      title: "Tournament House Fees",
      icon: BarChart2,
      href: "/tournament-house-fees",
    },
    {
      title: "Tips Management",
      icon: DollarSign,
      href: "/tips",
      hasSubmenu: true,
      subItems: [
        { title: "Dealer Tips", href: "/dealer-tips", icon: Users },
        { title: "Waitress Tips", href: "/waitress-tips", icon: Coffee },
        { title: "Floor Tips", href: "/floor-tips", icon: Gift },
        { title: "Other Tips", href: "/other-tips", icon: DollarSign },
      ],
    },
    {
      title: "Poker Cash Box",
      icon: CreditCardIcon,
      href: "/poker-cash-box",
    },
    {
      title: "Tournament Poker Chips",
      icon: CoinsIcon,
      href: "/tournament-poker-chips",
    },
    
    
  ]

  const siteManagementItems = [
    {
      title: "EPL Players",
      icon: User,
      href: "/players",
      hasSubmenu: true,
      subItems: [
        { title: "Players Profile", href: "/player-profiles", icon: BarChart2 },
        // { title: "Leaderboard Admin", href: "/leaderboard-admin", icon: Trophy },
      ],
    },
    {
      title: "SMS",
      icon: MessageSquare,
      href: "/sms-logs",
    },
  ]

  const isAdminLeaderboard = isAdminLeaderboardRole(userRole)
  const isAdminOrManager = isManagementRole(userRole)
  const canAccessEplPlayers = isEplPlayersAccessRole(userRole)

  const leaderboardOnlyItems = [
    {
      title: "Tournament Leaderboard",
      icon: Trophy,
      href: "/tournament-leaderboard",
    },
  ]

  return (
    <aside
      className={`bg-gradient-to-b from-gray-50 to-white shadow-xl flex flex-col h-screen fixed top-16 left-0 transition-all duration-300 ${
        isCollapsed ? "w-[80px]" : "w-[300px]"
      } border-r border-gray-200 z-40`}
      onMouseEnter={() => setIsCollapsed(false)}
      onMouseLeave={() => {
        setIsCollapsed(true)
        handleItemLeave()
      }}
    >
      <div className="mb-20 flex flex-col h-full overflow-y-auto" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        <div className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold text-sm tracking-wider">{!isCollapsed ? "LEADERBOARD" : "•••"}</div>
        <nav className="space-y-1 p-4">
          {leaderboardOnlyItems.map((item) => (
            <NavItem
              key={item.href}
              icon={item.icon}
              label={item.title}
              href={item.href}
              isCollapsed={isCollapsed}
              isActive={location.pathname === item.href}
            />
          ))}
        </nav>

        {!isAdminLeaderboard && isAdminOrManager && (
          <>
            <div className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold text-sm tracking-wider">{!isCollapsed ? "MANAGEMENT" : "•••"}</div>
            <nav className="space-y-1 p-4">
              {managementItems.map((item) => (
                <div key={item.href} className="relative">
                  <NavItem
                    icon={item.icon}
                    label={item.title}
                    href={item.href}
                    isCollapsed={isCollapsed}
                    isActive={location.pathname === item.href}
                    hasSubmenu={item.hasSubmenu}
                    isOpen={hoveredItem === item.title}
                    onMouseEnter={() => item.hasSubmenu && handleItemHover(item.title)}
                    onMouseLeave={() => item.hasSubmenu && handleItemLeave()}
                  />
                  {item.hasSubmenu && hoveredItem === item.title && !isCollapsed && (
                    <div 
                      className="ml-6 mt-1 space-y-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                      onMouseEnter={() => handleDropdownEnter(item.title)}
                      onMouseLeave={() => handleDropdownLeave()}
                    >
                      <div className="py-2">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.href}
                            to={subItem.href}
                            className={`flex items-center px-4 py-2 mx-2 rounded-md text-sm transition-all duration-200 ${
                              location.pathname === subItem.href 
                                ? "bg-gradient-to-r from-gray-600 to-gray-500 text-white" 
                                : "text-gray-600 hover:bg-gray-100 hover:text-gray-700"
                            }`}
                          >
                            {subItem.icon && <subItem.icon size={16} className={`mr-3 ${location.pathname === subItem.href ? "text-white" : "text-amber-400"}`} strokeWidth={1.5} />}
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </>
        )}

        {!isAdminLeaderboard && (
          <>
        <div className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold text-sm tracking-wider">{!isCollapsed ? "TRANSACTIONS" : "•••"}</div>

        <nav className="space-y-1 p-4">
          {transactionItems.map((item) => (
            <div key={item.href} className="relative">
              <NavItem
                icon={item.icon}
                label={item.title}
                href={item.href}
                isCollapsed={isCollapsed}
                isActive={location.pathname === item.href}
                hasSubmenu={item.hasSubmenu}
                isOpen={hoveredItem === item.title}
                onMouseEnter={() => item.hasSubmenu && handleItemHover(item.title)}
                onMouseLeave={() => item.hasSubmenu && handleItemLeave()}
              />
              {item.hasSubmenu && hoveredItem === item.title && !isCollapsed && (
                <div 
                  className="ml-6 mt-1 space-y-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                  onMouseEnter={() => handleDropdownEnter(item.title)}
                  onMouseLeave={() => handleDropdownLeave()}
                >
                  <div className="py-2">
                    {item.subItems.map((subItem) => (
                      <Link
                        key={subItem.href}
                        to={subItem.href}
                        className={`flex items-center px-4 py-2 mx-2 rounded-md text-sm transition-all duration-200 ${
                          location.pathname === subItem.href 
                            ? "bg-gradient-to-r from-gray-600 to-gray-500 text-white" 
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-700"
                        }`}
                      >
                        {subItem.icon && <subItem.icon size={16} className={`mr-3 ${location.pathname === subItem.href ? "text-white" : "text-amber-400"}`} strokeWidth={1.5} />}
                        {subItem.title}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {canAccessEplPlayers && (
          <>
            <div className="px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold text-sm tracking-wider">{!isCollapsed ? "TABLE TRACKER" : "•••"}</div>

            <nav className="space-y-1 p-4">
              {siteManagementItems.map((item) => (
                <div key={item.href} className="relative">
                  <NavItem
                    icon={item.icon}
                    label={item.title}
                    href={item.href}
                    isCollapsed={isCollapsed}
                    isActive={location.pathname === item.href}
                    hasSubmenu={item.hasSubmenu}
                    isOpen={hoveredItem === item.title}
                    onMouseEnter={() => item.hasSubmenu && handleItemHover(item.title)}
                    onMouseLeave={() => item.hasSubmenu && handleItemLeave()}
                  />
                  {item.hasSubmenu && hoveredItem === item.title && !isCollapsed && (
                    <div 
                      className="ml-6 mt-1 space-y-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                      onMouseEnter={() => handleDropdownEnter(item.title)}
                      onMouseLeave={() => handleDropdownLeave()}
                    >
                      <div className="py-2">
                        {item.subItems.map((subItem) => (
                          <Link
                            key={subItem.href}
                            to={subItem.href}
                            className={`flex items-center px-4 py-2 mx-2 rounded-md text-sm transition-all duration-200 ${
                              location.pathname === subItem.href 
                                ? "bg-gradient-to-r from-gray-600 to-gray-500 text-white" 
                                : "text-gray-600 hover:bg-gray-100 hover:text-gray-700"
                            }`}
                          >
                            {subItem.icon && <subItem.icon size={16} className={`mr-3 ${location.pathname === subItem.href ? "text-white" : "text-amber-400"}`} strokeWidth={1.5} />}
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </>
        )}
          </>
        )}
      </div>
    </aside>
  )
}